import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getDirection, normalizeLanguage } from '../i18n/config';

/**
 * Owns <html lang> and <html dir> for the whole app.
 *
 * These two attributes used to be declared inside <SEO>, which only nine
 * pages render — the landing page, the legal pages, the auth pages and the
 * dashboard. That looked harmless and was not, because Helmet does not merely
 * set the attributes it is given: it records them in `data-rh` and REMOVES
 * every recorded attribute that the currently mounted Helmets no longer
 * declare (see updateAttributes in react-helmet-async).
 *
 * So navigating from a page that has <SEO> to one that does not — /dashboard
 * to /customers, say — unmounted the only Helmet declaring them and stripped
 * `lang` and `dir` off <html> entirely. Every logical property in the app
 * (the sidebar's `start-0`, the header's `start-60`, `ps`/`pe`, `border-e`),
 * every `rtl:` variant and the Arabic font stack keyed on html[lang] then
 * fell back to their LTR defaults: an Arabic UI laid out left-to-right, with
 * the sidebar on the wrong edge.
 *
 * Mounting this above <Routes> means the declaration exists on every route,
 * so there is nothing for Helmet to reclaim. It is also the piece that lets
 * react-snap serialise the right pair into each prerendered snapshot —
 * /ar/terms is baked as dir="rtl" rather than inheriting the crawl's default.
 *
 * The pre-paint script in public/index.html still sets both before the first
 * frame, and i18n's languageChanged listener still keeps them in step outside
 * React; this stops React taking them away again.
 */
export default function HtmlLangDir() {
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  return (
    <Helmet>
      <html lang={language} dir={getDirection(language)} />
    </Helmet>
  );
}
